import { useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Select } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { getSchoolInfo } from "@/services/schoolService";
import type { Enrollment, SchoolClass, Student } from "@/types";
import { exportStudentCardsPdf, type CardStudent } from "@/utils/studentCardPdf";

interface Props {
  open: boolean;
  onClose: () => void;
  students: Student[];
  /** Inscriptions (déjà limitées aux cycles visibles). */
  enrollments: Enrollment[];
  classes: SchoolClass[];
  schoolYear: string;
}

/** Impression des cartes scolaires recto-verso d'une classe (ou de toute l'école), 4 élèves par page A4. */
export default function StudentCardsModal({ open, onClose, students, enrollments, classes, schoolYear }: Props) {
  const { notify } = useToast();
  const [classId, setClassId] = useState("all");
  const [printing, setPrinting] = useState(false);

  const active = useMemo(
    () => enrollments.filter((e) => e.schoolYear === schoolYear && e.status === "ACTIVE"),
    [enrollments, schoolYear],
  );
  const classList = useMemo(
    () =>
      classes
        .filter((c) => active.some((e) => e.classId === c.id))
        .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.name.localeCompare(b.name)),
    [classes, active],
  );

  const cards = useMemo<CardStudent[]>(() => {
    const byId = new Map(students.map((s) => [s.id, s]));
    const className = (id: string) => classes.find((c) => c.id === id)?.name ?? "—";
    const order = new Map(classList.map((c, i) => [c.id, i]));
    return active
      .filter((e) => classId === "all" || e.classId === classId)
      .flatMap((e) => {
        const s = byId.get(e.studentId);
        return s
          ? [
              {
                card: {
                  firstName: s.firstName,
                  lastName: s.lastName,
                  matricule: s.matricule,
                  gender: s.gender,
                  birthDate: s.birthDate,
                  birthPlace: s.birthPlace,
                  className: className(e.classId),
                  photo: s.photo,
                },
                rank: order.get(e.classId) ?? 99,
              },
            ]
          : [];
      })
      .sort((a, b) => a.rank - b.rank || a.card.lastName.localeCompare(b.card.lastName) || a.card.firstName.localeCompare(b.card.firstName))
      .map(({ card }) => card);
  }, [active, students, classes, classList, classId]);

  const withoutPhoto = cards.filter((c) => !c.photo).length;

  const print = async () => {
    setPrinting(true);
    try {
      const label = classId === "all" ? "ecole" : (classes.find((c) => c.id === classId)?.name ?? "classe");
      await exportStudentCardsPdf(
        cards,
        await getSchoolInfo(),
        schoolYear,
        `cartes-${label.replace(/[^\p{L}\p{N}]+/gu, "-")}-${schoolYear}.pdf`,
      );
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Impression impossible.", "error");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cartes scolaires"
      subtitle={`Année ${schoolYear}`}
      icon="ri-bank-card-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-file-pdf-2-line" onClick={print} disabled={printing || cards.length === 0}>
            {printing ? "Préparation..." : `Générer ${cards.length} carte${cards.length > 1 ? "s" : ""}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select label="Classe" value={classId} onChange={(e) => setClassId(e.target.value)}>
          <option value="all">Toutes les classes</option>
          {classList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <p className="text-xs text-foreground-500">
          Format carte bancaire (85 × 54 mm), recto et verso côte à côte : 4 élèves par page A4, à découper le
          long des pointillés puis à coller ou plastifier dos à dos. Un QR code au recto reprend le matricule, le
          nom et la classe. Seuls les élèves inscrits pour {schoolYear} ont une carte.
        </p>
        {withoutPhoto > 0 && (
          <p className="flex items-start gap-2 rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-800">
            <i className="ri-image-line mt-0.5"></i>
            {withoutPhoto} élève{withoutPhoto > 1 ? "s n'ont" : " n'a"} pas de photo : ses initiales seront imprimées à la
            place. Ajoutez la photo depuis la fiche de l'élève.
          </p>
        )}
      </div>
    </Modal>
  );
}
